import { useTranslation } from 'react-i18next'
import { useState } from 'react'

import { AcknowledgeModal, ConfirmationModal, FormField, Modal, TextArea } from '@percent/lemonade'
import { RejectCandidateModalProps } from './RejectCandidateModal.types'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useMutation } from '@percent/utility'

export function RejectCandidateModal({
  isOpen,
  programId,
  candidate,
  refresh,
  onClose
}: Readonly<RejectCandidateModalProps>) {
  const [modalState, setModalState] = useState<'success' | 'error' | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')

  const { grantProgramService } = useServices()
  const { t } = useTranslation()

  const [{ isPending }, { apiFunc: rejectCandidate }] = useMutation(
    grantProgramService.rejectProgramCandidate,
    () => {
      refresh()
      setModalState('success')
    },
    () => setModalState('error')
  )

  const handleClose = () => {
    onClose()
    setModalState(null)
    setRejectionReason('')
  }

  const successModal = modalState === 'success' && (
    <AcknowledgeModal
      result="positive"
      title={t('grants.programDetails.candidates.modal.reject.success.title')}
      description={t('grants.programDetails.candidates.modal.reject.success.text', {
        candidateName: candidate?.organisation?.name
      })}
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="reject-program-success-modal"
      buttonTestId="close-reject-program-success-modal"
    />
  )

  const errorModal = modalState === 'error' && (
    <AcknowledgeModal
      result="negative"
      title={t('grants.programDetails.candidates.modal.reject.error.title')}
      description={t('grants.programDetails.candidates.modal.reject.error.description')}
      buttonText={t('button.done')}
      handleClose={handleClose}
      viewTestId="reject-program-error-modal"
      buttonTestId="close-reject-program-error-modal"
    />
  )

  return (
    <Modal open={isOpen} onClose={onClose}>
      {successModal || errorModal || (
        <ConfirmationModal
          title={t('grants.programDetails.candidates.actions.reject')}
          loading={isPending}
          primaryButtonText={t('grants.programDetails.candidates.modal.reject.confirm')}
          primaryBtnTestId="reject-program-modal-button"
          secondaryButtonText={t('button.cancel')}
          handleSubmit={() =>
            rejectCandidate({
              programId,
              candidateId: candidate?.id ?? '',
              rejectionReason: rejectionReason.trim() || undefined
            })
          }
          handleClose={handleClose}
        >
          {t('grants.programDetails.candidates.modal.reject.description', {
            candidateName: candidate?.organisation?.name
          })}
          <FormField label={t('grants.programDetails.candidates.modal.reject.reason.label')} necessity="optional">
            <TextArea
              name="rejectionReason"
              value={rejectionReason}
              placeholder={t('grants.programDetails.candidates.modal.reject.reason.placeholder')}
              maxLength={255}
              onChange={event => setRejectionReason(event.target.value)}
              data-testid="reject-program-reason-input"
            />
          </FormField>
        </ConfirmationModal>
      )}
    </Modal>
  )
}
